import React, { useState, useEffect, useCallback } from 'react';
import { donationsAPI, familiesAPI } from '../api/apiService';
import styles from './AdminDonationsPage.module.css';

const AdminDonationsPage = () => {
  const [donations, setDonations] = useState([]);
  const [families, setFamilies] = useState([]); // For linking donations
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddManualForm, setShowAddManualForm] = useState(false);
  const [manualDonation, setManualDonation] = useState({
    amount: '', // in cents
    currency: 'USD',
    donorName: '',
    donorEmail: '',
    donationTarget: '',
    paymentMethod: 'Manual',
    familyId: '', // Optional
    notes: '',
    timestamp: new Date().toISOString().slice(0, 16), // For datetime-local input
  });

  const [linkDonationData, setLinkDonationData] = useState({
    donationId: '',
    familyId: ''
  });
  const [showLinkForm, setShowLinkForm] = useState(null); // Stores donationId to link

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [donationsResult, familiesResult] = await Promise.all([
        donationsAPI.getAll(),
        familiesAPI.list() // To populate family dropdown
      ]);
      setDonations(donationsResult.donations || []);
      setFamilies(familiesResult.families || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(err.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleManualDonationChange = (e) => {
    const { name, value } = e.target;
    if (name === "amount") {
        // Assuming user inputs dollars, convert to cents for storage
        setManualDonation(prev => ({ ...prev, [name]: Math.round(parseFloat(value) * 100) }));
    } else {
        setManualDonation(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddManualDonation = async (e) => {
    e.preventDefault();
    setError(null);
    if (!manualDonation.amount || manualDonation.amount <= 0) {
        setError("Please enter a valid positive amount.");
        return;
    }
    try {
      // Convert timestamp from local datetime string to ISO string for the function
      const submissionData = {
        ...manualDonation,
        timestamp: new Date(manualDonation.timestamp).toISOString()
      };
      await donationsAPI.addManual(submissionData);
      setShowAddManualForm(false);
      setManualDonation({ amount: '', currency: 'USD', donorName: '', donorEmail: '', donationTarget: '', paymentMethod: 'Manual', familyId: '', notes: '', timestamp: new Date().toISOString().slice(0, 16) });
      fetchAllData(); // Refresh list
    } catch (err) {
      console.error("Error adding manual donation:", err);
      setError(err.message || "Failed to add manual donation.");
    }
  };
  
  const handleLinkDonationChange = (e) => {
    setLinkDonationData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLinkDonation = async (e) => {
    e.preventDefault();
    if (!linkDonationData.donationId || !linkDonationData.familyId) {
      setError("Both donation and family must be selected.");
      return;
    }
    setError(null);
    try {
      await donationsAPI.linkToFamily(linkDonationData.donationId, linkDonationData.familyId);
      setShowLinkForm(null);
      setLinkDonationData({ donationId: '', familyId: '' });
      fetchAllData(); // Refresh list
    } catch (err) {
      console.error("Error linking donation:", err);
      setError(err.message || "Failed to link donation.");
    }
  };
  
  const openLinkFormForDonation = (donation) => {
    setShowLinkForm(donation.donationId);
    setLinkDonationData({ donationId: donation.donationId, familyId: donation.familyId || '' });
    setShowAddManualForm(false);
  };

  const getFamilyName = (familyId) => {
    const family = families.find(f => f.familyId === familyId);
    return family ? `${family.lastName} (Account: ${family.accountEmail})` : 'N/A';
  };

  if (loading) return <p>Loading donations...</p>;

  return (
    <div className={styles.donationsContainer}>
      <h2>Manage Donations</h2>
      {error && <p className={styles.error}>{error}</p>}

      <button onClick={() => { setShowAddManualForm(!showAddManualForm); setShowLinkForm(null); }} className={styles.addButton}>
        {showAddManualForm ? 'Cancel Manual Donation' : 'Add Manual Donation'}
      </button>

      {showAddManualForm && (
        <form onSubmit={handleAddManualDonation} className={styles.donationForm}>
          <h3>Add Manual Donation</h3>
          <div><label>Amount (USD):</label><input type="number" name="amount" step="0.01" value={manualDonation.amount ? manualDonation.amount / 100 : ''} onChange={e => setManualDonation(prev => ({ ...prev, amount: Math.round(parseFloat(e.target.value) * 100) }))} required /></div>
          <div><label>Currency:</label><input type="text" name="currency" value={manualDonation.currency} onChange={handleManualDonationChange} required /></div>
          <div><label>Donor Name:</label><input type="text" name="donorName" value={manualDonation.donorName} onChange={handleManualDonationChange} /></div>
          <div><label>Donor Email:</label><input type="email" name="donorEmail" value={manualDonation.donorEmail} onChange={handleManualDonationChange} /></div>
          <div><label>Donation Target:</label><input type="text" name="donationTarget" value={manualDonation.donationTarget} onChange={handleManualDonationChange} /></div>
          <div><label>Payment Method:</label><input type="text" name="paymentMethod" value={manualDonation.paymentMethod} onChange={handleManualDonationChange} /></div>
          <div><label>Date & Time:</label><input type="datetime-local" name="timestamp" value={manualDonation.timestamp} onChange={handleManualDonationChange} required /></div>
          <div>
            <label>Family (Optional):</label>
            <select name="familyId" value={manualDonation.familyId} onChange={handleManualDonationChange}>
              <option value="">Select Family</option>
              {families.map(f => <option key={f.familyId} value={f.familyId}>{f.lastName} ({f.accountEmail})</option>)}
            </select>
          </div>
          <div><label>Notes:</label><textarea name="notes" value={manualDonation.notes} onChange={handleManualDonationChange}></textarea></div>
          <button type="submit">Add Donation</button>
        </form>
      )}

      {showLinkForm && (
        <form onSubmit={handleLinkDonation} className={styles.donationForm}>
          <h3>Link Donation (ID: {showLinkForm}) to Family</h3>
          <input type="hidden" name="donationId" value={showLinkForm} />
          <div>
            <label>Family:</label>
            <select name="familyId" value={linkDonationData.familyId} onChange={handleLinkDonationChange} required>
              <option value="">Select Family</option>
              {families.map(f => <option key={f.familyId} value={f.familyId}>{f.lastName} ({f.accountEmail})</option>)}
            </select>
          </div>
          <button type="submit">Link Donation</button>
          <button type="button" onClick={() => setShowLinkForm(null)}>Cancel</button>
        </form>
      )}

      <table className={styles.donationsTable}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Date</th>
            <th>Amount</th>
            <th>Donor Name</th>
            <th>Donor Email</th>
            <th>Family</th>
            <th>Method</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {donations.map(donation => (
            <tr key={donation.donationId}>
              <td title={donation.donationId}>{donation.donationId.substring(0, 8)}...</td>
              <td>{new Date(donation.timestamp).toLocaleString()}</td>
              <td>${(donation.amount / 100).toFixed(2)} {donation.currency}</td>
              <td>{donation.donorName || '-'}</td>
              <td>{donation.donorEmail || '-'}</td>
              <td>{donation.familyId ? getFamilyName(donation.familyId) : <button onClick={() => openLinkFormForDonation(donation)} className={styles.linkButton}>Link</button>}</td>
              <td>{donation.paymentMethod}</td>
              <td>{donation.status}</td>
              <td>
                {!donation.familyId && donation.paymentMethod !== 'Stripe' && /* Only allow linking if not already linked and not a Stripe one (which tries auto-link) */
                  <button onClick={() => openLinkFormForDonation(donation)} className={styles.linkButtonSmall}>Link</button>
                }
                 {/* Add other actions like view details, refund (if applicable) etc. */}
              </td>
            </tr>
          ))}
          {donations.length === 0 && !loading && (
            <tr>
              <td colSpan="9">No donations found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AdminDonationsPage;
